import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useExcIceCorpactionsV1ActionQueryServiceActionGet } from '@interxfi/api-client/generated/hooks';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import { ReadonlyList, type ReadonlyListItem } from '@interxfi/design-system/components/list';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import type { ExcIceCorpactionsV1ActionState } from '@interxfi/types';
import { STABLE_EMPTY_ARRAY_REF } from '@interxfi/utilities/array/stable-empty';
import { exhaustiveCheck } from '@interxfi/utilities/types/exhaustive';
import { IceCaActionCapitalChangeCostBasisDetailsTable } from '../../../../../../../../../../features/vendor/ice/corporate-action/action/event-type/capital-change/cost-basis/ice-ca-action-capital-change-cost-basis-details-table';
import { IceCaActionCapitalChangeDetailsTable } from '../../../../../../../../../../features/vendor/ice/corporate-action/action/event-type/capital-change/details/ice-ca-action-capital-change-details-table';
import { getGroupedIceCaCapitalChangeListItems } from '../../../../../../../../../../features/vendor/ice/corporate-action/action/event-type/capital-change/ice-ca-action-capital-change-list-items';
import { IceCaActionConversionItemsTable } from '../../../../../../../../../../features/vendor/ice/corporate-action/action/event-type/conversion-list/ice-ca-action-conversion-items-table';
import { IceCaActionDividendAmountsTable } from '../../../../../../../../../../features/vendor/ice/corporate-action/action/event-type/dividend/ice-ca-action-dividend-amounts-table';
import { getGroupedIceCaDividendListItems } from '../../../../../../../../../../features/vendor/ice/corporate-action/action/event-type/dividend/ice-ca-action-dividend-list-items';
import { IceCaActionEarningsListItemsTable } from '../../../../../../../../../../features/vendor/ice/corporate-action/action/event-type/earnings-list/ice-ca-action-earnings-list-items-table';
import { IceCaActionIdentifierChangeItemsTable } from '../../../../../../../../../../features/vendor/ice/corporate-action/action/event-type/identifier-change/ice-ca-identifier-change-items-table';
import { getGroupedIceCaInformationAnnouncementListItems } from '../../../../../../../../../../features/vendor/ice/corporate-action/action/event-type/information-announcement/ice-ca-action-information-announcement-list-items';
import { IceCaActionListingListItemsTable } from '../../../../../../../../../../features/vendor/ice/corporate-action/action/event-type/listing-list/ice-ca-action-listing-list-items-table';
import { IceCaActionMergerAcquisitionItemsTable } from '../../../../../../../../../../features/vendor/ice/corporate-action/action/event-type/merger-acquisition-list/ice-ca-action-merger-acquisition-items-table';
import { IceCaActionOutstandingSharesItemsTable } from '../../../../../../../../../../features/vendor/ice/corporate-action/action/event-type/outstanding-shares-list/ice-ca-action-outstanding-shares-items-table';
import { getGroupedIceCaPaymentListItems } from '../../../../../../../../../../features/vendor/ice/corporate-action/action/event-type/payment/ice-ca-action-payment-list-items';
import { IceCaActionRedemptionListItemsTable } from '../../../../../../../../../../features/vendor/ice/corporate-action/action/event-type/redemption-list/ice-ca-action-redemption-list-items-table';
import { IceCaActionRepurchaseListItemsTable } from '../../../../../../../../../../features/vendor/ice/corporate-action/action/event-type/repurchase-list/ice-ca-action-repurchase-list-items-table';
import { getGroupedIceCaShareholderMeetingListItems } from '../../../../../../../../../../features/vendor/ice/corporate-action/action/event-type/shareholder-meeting/ice-ca-action-shareholder-meeting-list-items';
import { IceCaActionEntityMenu } from '../../../../../../../../../../features/vendor/ice/corporate-action/action/ice-ca-action-entity-menu';
import { IceCaActionEventSummary } from '../../../../../../../../../../features/vendor/ice/corporate-action/action/ice-ca-action-event';
import { getIceCaActionStateListItems } from '../../../../../../../../../../features/vendor/ice/corporate-action/action/ice-ca-action-list-items';
import { IceCaActionMarketsTable } from '../../../../../../../../../../features/vendor/ice/corporate-action/action/ice-ca-action-markets-table';
import { getIceCaActionDetailPath } from '../../../../../../../../../../features/vendor/ice/corporate-action/action/ice-ca-action-routing';
import { IceCaActionStatusPill } from '../../../../../../../../../../features/vendor/ice/corporate-action/action/ice-ca-action-status-pill';
import { IceCaActionTaxationTaxableAmountsTable } from '../../../../../../../../../../features/vendor/ice/corporate-action/action/taxation-details/ice-ca-action-taxation-taxable-amounts-table';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/vendor/_vendor/ice/corporate-action/_action/$eventId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `ice-ca-action-${params.eventId}`,
        label: 'Action',
        to: getIceCaActionDetailPath(params.eventId),
        suffix: <CopyButton content={params.eventId} label="Copy Event ID" tooltip={{ copy: params.eventId, copied: 'Copied Event ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('ICE Corporate Action'),
  component: IceCaActionDetail,
});

function EventTypeNode({ data }: { data: ExcIceCorpactionsV1ActionState | undefined }) {
  if (!data?.data.record?.eventType) {
    return null;
  }

  const items = getIceCaActionStateListItems(data);

  if ('capitalChange' in data.data.record.eventType) {
    const groupedItems = getGroupedIceCaCapitalChangeListItems(
      'capitalChange',
      data.data.record.eventType.capitalChange,
      'capitalChange' in items.data.record.eventType ? items.data.record.eventType.capitalChange.value : undefined,
    );

    return (
      <VerticalStack gap={7}>
        <AppReadonlyListStack groups={groupedItems} titleGap={4} />

        <VerticalStack gap={4}>
          <Typography.Level3>Details</Typography.Level3>
          <IceCaActionCapitalChangeDetailsTable details={data.data.record.eventType.capitalChange.details ?? STABLE_EMPTY_ARRAY_REF} />
        </VerticalStack>

        <VerticalStack gap={4}>
          <Typography.Level3>Cost Basis Details</Typography.Level3>
          <IceCaActionCapitalChangeCostBasisDetailsTable
            details={data.data.record.eventType.capitalChange.costBasisDetails ?? STABLE_EMPTY_ARRAY_REF}
          />
        </VerticalStack>
      </VerticalStack>
    );
  }

  if ('dividend' in data.data.record.eventType) {
    const dividendItems = 'dividend' in items.data.record.eventType ? items.data.record.eventType.dividend.value : undefined;
    const groupedItems = [
      ...getGroupedIceCaDividendListItems('dividend', data.data.record.eventType.dividend, dividendItems),
      ...(dividendItems?.reinvestmentDetails
        ? ([
            {
              id: 'reinvestmentDetails',
              title: 'Reinvestment Details',
              items: [
                dividendItems.reinvestmentDetails.electionStartDate,
                dividendItems.reinvestmentDetails.electionEndDate,
                dividendItems.reinvestmentDetails.dripIndicator,
                dividendItems.reinvestmentDetails.reinvestmentPrice,
                dividendItems.reinvestmentDetails.reinvestmentPriceCurrency,
              ],
            },
          ] as const satisfies ReadonlyListGroup[])
        : []),
    ];

    return (
      <VerticalStack gap={7}>
        <AppReadonlyListStack groups={groupedItems} titleGap={4} />

        <VerticalStack gap={4}>
          <Typography.Level3>Amount Details</Typography.Level3>

          <IceCaActionDividendAmountsTable details={data.data.record.eventType.dividend.amountDetails ?? STABLE_EMPTY_ARRAY_REF} />
        </VerticalStack>

        {data.data.record.eventType.dividend.taxationDetails && dividendItems?.taxationDetails ? (
          <VerticalStack gap={4}>
            <Typography.Level3>Taxation Details</Typography.Level3>

            <ReadonlyList
              items={[
                dividendItems.taxationDetails.taxStatus,
                dividendItems.taxationDetails.taxCode,
                dividendItems.taxationDetails.taxRate,
                dividendItems.taxationDetails.dividendTaxMarker,
                dividendItems.taxationDetails.taxAmountPayable,
                dividendItems.taxationDetails.taxAmountPayableCurrency,
                dividendItems.taxationDetails.taxAmountUnits,
              ]}
            />

            <VerticalStack gap={4}>
              <Typography.Level3>Taxable Amount Details</Typography.Level3>

              <IceCaActionTaxationTaxableAmountsTable
                details={data.data.record.eventType.dividend.taxationDetails.taxableAmountDetails ?? STABLE_EMPTY_ARRAY_REF}
              />
            </VerticalStack>
          </VerticalStack>
        ) : null}
      </VerticalStack>
    );
  }

  if ('mergerAcquisitionList' in data.data.record.eventType) {
    return (
      <VerticalStack gap={4}>
        <Typography.Level3>Merger &amp; Acquisition Items</Typography.Level3>

        <IceCaActionMergerAcquisitionItemsTable
          items={data.data.record.eventType.mergerAcquisitionList.mergersAcquisitionsItems ?? STABLE_EMPTY_ARRAY_REF}
        />
      </VerticalStack>
    );
  }

  if ('payment' in data.data.record.eventType) {
    const paymentItems = 'payment' in items.data.record.eventType ? items.data.record.eventType.payment.value : undefined;
    const groupedItems = [
      ...getGroupedIceCaPaymentListItems('payment', data.data.record.eventType.payment, paymentItems),
      ...(paymentItems?.reinvestmentDetails
        ? ([
            {
              id: 'reinvestmentDetails',
              title: 'Reinvestment Details',
              items: [
                paymentItems.reinvestmentDetails.electionStartDate,
                paymentItems.reinvestmentDetails.electionEndDate,
                paymentItems.reinvestmentDetails.dripIndicator,
                paymentItems.reinvestmentDetails.reinvestmentPrice,
                paymentItems.reinvestmentDetails.reinvestmentPriceCurrency,
              ],
            },
          ] as const satisfies ReadonlyListGroup[])
        : []),
    ];

    return (
      <VerticalStack gap={7}>
        <AppReadonlyListStack groups={groupedItems} titleGap={4} />

        {data.data.record.eventType.payment.taxationDetails && paymentItems?.taxationDetails ? (
          <VerticalStack gap={4}>
            <Typography.Level3>Taxation Details</Typography.Level3>

            <ReadonlyList
              items={[
                paymentItems.taxationDetails.taxStatus,
                paymentItems.taxationDetails.taxCode,
                paymentItems.taxationDetails.taxRate,
                paymentItems.taxationDetails.dividendTaxMarker,
                paymentItems.taxationDetails.taxAmountPayable,
                paymentItems.taxationDetails.taxAmountPayableCurrency,
                paymentItems.taxationDetails.taxAmountUnits,
              ]}
            />

            <VerticalStack gap={4}>
              <Typography.Level3>Taxable Amount Details</Typography.Level3>

              <IceCaActionTaxationTaxableAmountsTable
                details={data.data.record.eventType.payment.taxationDetails.taxableAmountDetails ?? STABLE_EMPTY_ARRAY_REF}
              />
            </VerticalStack>
          </VerticalStack>
        ) : null}
      </VerticalStack>
    );
  }

  if ('earningsList' in data.data.record.eventType) {
    return (
      <VerticalStack gap={4}>
        <Typography.Level3>Earnings Items</Typography.Level3>

        <IceCaActionEarningsListItemsTable items={data.data.record.eventType.earningsList.earningsItems ?? STABLE_EMPTY_ARRAY_REF} />
      </VerticalStack>
    );
  }

  if ('redemptionList' in data.data.record.eventType) {
    return (
      <VerticalStack gap={4}>
        <Typography.Level3>Redemption Items</Typography.Level3>

        <IceCaActionRedemptionListItemsTable items={data.data.record.eventType.redemptionList.redemptionItems ?? STABLE_EMPTY_ARRAY_REF} />
      </VerticalStack>
    );
  }

  if ('repurchaseList' in data.data.record.eventType) {
    return (
      <VerticalStack gap={4}>
        <Typography.Level3>Repurchase Items</Typography.Level3>

        <IceCaActionRepurchaseListItemsTable details={data.data.record.eventType.repurchaseList.repurchaseItems ?? STABLE_EMPTY_ARRAY_REF} />
      </VerticalStack>
    );
  }

  if ('informationAnnouncement' in data.data.record.eventType) {
    const groupedItems = getGroupedIceCaInformationAnnouncementListItems(
      'informationAnnouncement',
      data.data.record.eventType.informationAnnouncement,
      'informationAnnouncement' in items.data.record.eventType ? items.data.record.eventType.informationAnnouncement.value : undefined,
    );

    return <AppReadonlyListStack groups={groupedItems} titleGap={4} />;
  }

  if ('shareholderMeeting' in data.data.record.eventType) {
    const groupedItems = getGroupedIceCaShareholderMeetingListItems(
      'shareholderMeeting',
      data.data.record.eventType.shareholderMeeting,
      'shareholderMeeting' in items.data.record.eventType ? items.data.record.eventType.shareholderMeeting.value : undefined,
    );

    return <AppReadonlyListStack groups={groupedItems} titleGap={4} />;
  }

  if ('conversionList' in data.data.record.eventType) {
    return (
      <VerticalStack gap={4}>
        <Typography.Level3>Conversion Items</Typography.Level3>

        <IceCaActionConversionItemsTable items={data.data.record.eventType.conversionList.conversionItems ?? STABLE_EMPTY_ARRAY_REF} />
      </VerticalStack>
    );
  }

  if ('identifierChangeList' in data.data.record.eventType) {
    return (
      <VerticalStack gap={4}>
        <Typography.Level3>Identifier Change Items</Typography.Level3>

        <IceCaActionIdentifierChangeItemsTable
          items={data.data.record.eventType.identifierChangeList.identifierChangeItems ?? STABLE_EMPTY_ARRAY_REF}
        />
      </VerticalStack>
    );
  }

  if ('listingList' in data.data.record.eventType) {
    return (
      <VerticalStack gap={4}>
        <Typography.Level3>Listing Items</Typography.Level3>
        <IceCaActionListingListItemsTable items={data.data.record.eventType.listingList.listingItems ?? STABLE_EMPTY_ARRAY_REF} />
      </VerticalStack>
    );
  }

  if ('outstandingSharesList' in data.data.record.eventType) {
    return (
      <VerticalStack gap={4}>
        <Typography.Level3>Outstanding Shares Items</Typography.Level3>

        <IceCaActionOutstandingSharesItemsTable
          items={data.data.record.eventType.outstandingSharesList.outstandingSharesItems ?? STABLE_EMPTY_ARRAY_REF}
        />
      </VerticalStack>
    );
  }

  exhaustiveCheck(data.data.record.eventType?.['!type']);

  return null;
}

function getListItems(data: ExcIceCorpactionsV1ActionState | undefined) {
  const items = getIceCaActionStateListItems(data);

  return {
    left: [
      {
        id: 'lastSource',
        title: 'Last Source',
        items: [
          items.data.lastSource.eventId,
          items.data.lastSource.eventRevision,
          items.data.lastSource.updated,
          ...(items.data.lastSource.source
            ? [
                items.data.lastSource.source.bucket,
                items.data.lastSource.source.filename,
                items.data.lastSource.source.fileTimestamp,
                items.data.lastSource.source.recordNumber,
                items.data.lastSource.source.startOffset,
                items.data.lastSource.source.endOffset,
              ]
            : []),
        ],
      },
      {
        id: 'main',
        items: [
          items.data.record.actionType,
          items.data.record.eventStatus,
          items.data.record.announcementStatus,
          items.data.record.announcementDate,
          items.data.record.recordDate,
          items.data.record.entryDate,
          items.data.record.effectiveDate,
        ],
      },
      {
        id: 'instrument',
        title: 'Instrument',
        items: [
          items.data.record.instrument.id,
          items.data.record.instrument.cusip,
          items.data.record.instrument.symbol,
          items.data.record.instrument.name,
          items.data.record.instrument.securityType.description,
          items.data.record.instrument.securityType.subtype,
          items.data.record.instrument.countryOfIncorp,
          items.data.record.instrument.primaryExchange,
          items.data.record.instrument.homeMarket,
        ],
      },
    ] as const satisfies ReadonlyListGroup[],
    right: [items.metadata.createdAt, items.metadata.updatedAt, items.eventId] as const satisfies ReadonlyListItem[],
  } as const;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load ICE corporate action action', 'view this ICE corporate action action');

function IceCaActionDetail() {
  const { eventId } = Route.useParams();
  const request = { eventId } as const;
  const { data, error, isPending } = useExcIceCorpactionsV1ActionQueryServiceActionGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.action), [data?.action]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <IceCaActionStatusPill isPending={showPending} size="large" status={data?.action?.status} />
          </HorizontalStack>

          <IceCaActionEntityMenu {...request} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <IceCaActionEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      {data?.action?.data?.record?.eventType ? (
        <PageLayout.Section>
          <EventTypeNode data={data.action} />
        </PageLayout.Section>
      ) : null}

      {data?.action?.data?.record?.marketDetails ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Market Details</Typography.Level3>

            <IceCaActionMarketsTable markets={data.action.data.record.marketDetails} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}
    </PageLayout>
  );
}
